import React, {useState, useEffect, useRef, memo} from 'react'
import {VStack} from '@components/Stack'
import {rand} from 'src/utils'

function getMarkupFromString(instruction) {
  if (!instruction) return []

  // First handle code blocks
  const codeRegex = /`(.*?)`/g
  const linkRegex = /\[(.*?)\]\((.*?)\)/g

  // Split by both code blocks and links while preserving the delimiters
  const parts = instruction.split(/(\[.*?\]\(.*?\)|`.*?`)/g).filter(Boolean)

  return parts.map((part, index) => {
    // Handle code blocks
    if (part.startsWith('`') && part.endsWith('`')) {
      const code = part.slice(1, -1)
      return <code key={index}>{code}</code>
    }
    // Handle links
    if (part.match(linkRegex)) {
      const [, text, url] = linkRegex.exec(part)
      return (
        <a key={index} href={url} className="link" target="_blank">
          {text}
        </a>
      )
    }
    // Return regular text
    return part
  })
}

const LearnInstructions = memo(({levelData}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const interval = useRef()

  // reset currentIndex when levelData changes
  useEffect(() => {
    setCurrentIndex(0)
    return () => {
      clearInterval(interval.current)
    }
  }, [levelData])

  const showNextLine = () => {
    interval.current = setTimeout(() => {
      setCurrentIndex(currentIndex + 1)
    }, rand(500, 1000))
  }

  return (
    <VStack gap="0.8rem" align="stretch">
      {levelData.instructions.map((instruction, index) => {
        if (index > currentIndex) return null

        return (
          <Instruction
            key={`${levelData.name}-${index}`}
            data={instruction}
            isHighlighted={levelData.mainInstruction === index}
            onFinish={index === currentIndex ? showNextLine : undefined}
            index={index}
            currentIndex={currentIndex}
            lines={levelData.lines}
          />
        )
      })}
    </VStack>
  )
})

const Instruction = ({index, data, isHighlighted, lines, onFinish}) => {
  const [currentData, setCurrentData] = useState('')
  const length = useRef(0)
  const interval = useRef()
  const ref = useRef()

  function onTick() {
    setCurrentData(data.slice(0, length.current + 1))

    if (length.current >= data.length) {
      clearTimeout(interval.current)

      onFinish && onFinish()
    } else {
      length.current += 1
      const isWave =
        data[length.current] === ' ' && data[length.current - 1] === '\uDFFC'

      interval.current = setTimeout(
        onTick,
        isWave
          ? 1000
          : data[length.current] === ' '
          ? rand(40, 100)
          : rand(5, 20)
      )
    }
  }
  useEffect(() => {
    setCurrentData('')
    length.current = 0
    onTick()

    return () => {
      clearTimeout(interval.current)
    }
  }, [data])

  useEffect(() => {
    clearTimeout(interval.current)
    onTick()

    return () => {
      clearTimeout(interval.current)
    }
  }, [!!onFinish])

  // mouseEnterHandler
  const mouseEnterHandler = (e) => {
    if (!isHighlighted) return

    if (lines) {
      const linesEl = document.querySelectorAll('.cm-editor .cm-line')

      // map over lines and add highlight class to matching lines
      const attr = lines
        .map((lineNumber) => {
          return `L${lineNumber + 1}L`
        })
        .join('')

      document.querySelector('.cm-editor').setAttribute('highlight-lines', attr)

      return

      const e1 = linesEl[lines[0]]
      const e1Bound = e1.getBoundingClientRect()
      const e2Bound = ref.current.getBoundingClientRect()
      // console.log(e1, e1Bound, ref.current, e2Bound)
      const svgW = Math.abs(e1Bound.x - e2Bound.x)
      const svgH = Math.abs(e1Bound.y - e2Bound.y) + 100

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')

      svg.setAttributeNS(null, 'viewBox', `0 0 100 60`)
      svg.setAttributeNS(null, 'preserveAspectRatio', 'none')
      svg.setAttributeNS(null, 'class', 'instruction-arrow')
      svg.setAttributeNS(null, 'width', svgW - 100)
      svg.setAttributeNS(null, 'height', svgH)
      svg.setAttributeNS(
        null,
        'style',
        `top: ${e1Bound.bottom - 0}px; right: ${
          window.innerWidth - e2Bound.x - 0
        }px`
      )

      function makePath({d, classes}) {
        const p1 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        )
        p1.setAttributeNS(null, 'd', d)
        p1.setAttributeNS(null, 'class', classes)

        svg.appendChild(p1)
      }
      makePath({d: 'M 100 50 Q 40 60 5 2', classes: 'arrow-line'})
      makePath({d: 'M 5 2 L 15 5', classes: 'arrow-line tail'})
      makePath({d: 'M 5 2 L 6 17', classes: 'arrow-line tail'})
      document.body.append(svg)
      setTimeout(() => {
        svg.classList.add('draw')
      }, 100)
    }
  }

  const mouseLeaveHandler = () => {
    if (!isHighlighted) return

    document.querySelector('.cm-editor').setAttribute('highlight-lines', '')

    const arrow = document.querySelector('.instruction-arrow')

    if (arrow) {
      arrow.classList.add('undo')
      setTimeout(() => {
        arrow.remove()
      }, 500)
    }
  }

  return (
    <p
      ref={ref}
      style={{
        display: 'block',
        fontWeight: isHighlighted ? '600' : '500',
        color: isHighlighted ? 'var(--clr-heading)' : 'inherit',
        cursor: isHighlighted ? 'pointer' : 'normal',
        maxWidth: '70ch',
        letterSpacing: '0.2px',
      }}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      {getMarkupFromString(currentData).map((part, index) => (
        <React.Fragment key={index}>{part}</React.Fragment>
      ))}
    </p>
  )
}

export {LearnInstructions}
